import { AI_MESSAGE } from '@constants/ai';
import { AIModel, AIAutoAnswerSettings, SettingOption, useGetAIDocumentScanQuery, getAIModelName } from '@api/ai';
import { ROUTES } from '@constants/routes';
import { QUESTION } from '@constants/terminology';
import { BasicAlert } from '@g17eco/molecules/alert';
import { generateUrl } from '@routes/util';
import { isFinishedJob, isProcessingJob } from '@utils/background-job';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useGetDocumentsQuery } from '../../api/documents';
import Select from 'react-select';
import { SelectOption } from '../../types/select';
import { isStaff } from '../../selectors/user';
import { useAppSelector } from '../../reducers';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  initiativeId: string;
  handleSubmit: (settings: AIAutoAnswerSettings) => void;
  canAccessAIDocumentLibrary: boolean;
}

const SettingOptionDescription = ({
  title,
  titleClassName,
  children,
}: {
  title: string;
  titleClassName?: string;
  children: React.ReactNode;
}) => {
  return (
    <>
      <div className={titleClassName ?? 'text-ThemeWarningDark'}>{title}</div>
      <div className='text-ThemeTextMedium'>{children}</div>
    </>
  );
};

const DEFAULT_MODEL = AIModel.OpenAI_4o;

export const SettingsModal = ({ isOpen, toggle, initiativeId, handleSubmit, canAccessAIDocumentLibrary }: Props) => {
  const [settings, setSettings] = useState<AIAutoAnswerSettings>({
    [SettingOption.DocumentLibrary]: [],
    [SettingOption.OverwriteMetric]: false,
    [SettingOption.AIAgentMode]: false,
    [SettingOption.AIAgentModeOptions]: {
      model: AIModel.OpenAI_4o,
    },
  });

  const [skipPolling, setSkipPolling] = useState(false);
  const isStaffUser = useAppSelector(isStaff);
  const isAIAgentModeEnabled = isStaffUser;

  const { data, isSuccess, error } = useGetAIDocumentScanQuery(initiativeId, {
    skip: !isOpen || skipPolling,
    pollingInterval: 10000,
  });

  useEffect(() => {
    // Skip polling when not found job or job is finished
    if (isSuccess && (!data?.job || isFinishedJob(data.job))) {
      setSkipPolling(true);
    }
  }, [data, isSuccess]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    const name = e.target.name as SettingOption;
    setSettings((prevSettings) => ({ ...prevSettings, [name]: checked }));
  };

  const isScanning = !!data?.job && isProcessingJob(data.job);
  const documents = useGetDocumentsQuery({ initiativeId }, { skip: !isOpen });
  const documentOptions: SelectOption[] =
    documents.data?.documents.map((document) => ({
      label: `${document.title} ${document.processingServices?.some((processing) => processing.completedDate) ? '' : '(not processed)'}`,
      value: document._id,
      isDisabled: !document.processingServices?.some((processing) => processing.completedDate),
    })) ?? [];

  const modelOptions: SelectOption<AIModel>[] = Object.values(AIModel).map((model) => ({ value: model, label: getAIModelName(model) }));

  return (
    <Modal isOpen={isOpen} toggle={toggle} backdrop='static'>
      <ModalHeader toggle={toggle}>Auto-Answer Settings</ModalHeader>
      <ModalBody className='pb-0'>
        {error ? <BasicAlert type='danger'>{error.message}</BasicAlert> : null}
        <div className='text-md'>
          Automatically answer metrics using AI{' '}
          <span className='text-muted'>(will draft answers even when no data is available)</span>
        </div>

        <hr />
        {canAccessAIDocumentLibrary ? (
          <>
            <SettingOptionDescription title='Enhance with information from Document Library'>
              <div className='text-ThemeTextMedium text-sm'>
                {isScanning ? 'files currently processing - results may be incomplete' : ''}
              </div>
            </SettingOptionDescription>
            <Select
              className='w-100'
              isMulti={true}
              options={documentOptions}
              isDisabled={!canAccessAIDocumentLibrary}
              onChange={(values) =>
                setSettings((prevSettings) => ({
                  ...prevSettings,
                  [SettingOption.DocumentLibrary]: values.map((value) => value.value ?? ''),
                }))
              }
              value={
                documentOptions?.filter((option) =>
                  settings[SettingOption.DocumentLibrary].includes(option.value ?? ''),
                ) ?? null
              }
              data-testid={`auto-answer-${SettingOption.DocumentLibrary}`}
          />
          </>
        ) : (
          <SettingOptionDescription title='Enhance with information from Document Library'>
            <div className='text-ThemeTextMedium text-sm'>
              (to use enable AI access in{' '}
              <Link to={generateUrl(ROUTES.ACCOUNT_SETTINGS, { initiativeId, page: 'account-management' })}>
                Admin/Account Settings/Account Management
              </Link>
              )
            </div>
          </SettingOptionDescription>
        )}

        <FormGroup check className='mt-4 d-flex gap-2 align-items-center'>
          <Input
            type='checkbox'
            id={SettingOption.OverwriteMetric}
            name={SettingOption.OverwriteMetric}
            onChange={onChange}
            checked={settings[SettingOption.OverwriteMetric]}
            data-testid={`auto-answer-${SettingOption.OverwriteMetric}`}
          />
          <Label for={SettingOption.OverwriteMetric} className='m-0'>
            <SettingOptionDescription
              title={`Overwrite ${QUESTION.PLURAL} that already have content/answers`}
              titleClassName='text-ThemeWarningDark'
            >
              <div className='text-ThemeTextMedium text-sm'>
                (be careful with this one, it could overwrite content someone else has added)
              </div>
            </SettingOptionDescription>
          </Label>
        </FormGroup>

        {isAIAgentModeEnabled && (
          <>
            <FormGroup check className='mt-4 d-flex gap-2 align-items-center'>
              <Input
                type='checkbox'
                id={SettingOption.AIAgentMode}
                name={SettingOption.AIAgentMode}
                onChange={onChange}
                checked={settings[SettingOption.AIAgentMode]}
                data-testid={`auto-answer-${SettingOption.AIAgentMode}`}
                className='flex-shrink-0'
              />
              <div>
                <Label for={SettingOption.AIAgentMode} className='m-0'>
                  <SettingOptionDescription title='Use AI Agent Model'>
                    This is an experimental feature that allows you to use the AI Agent to answer questions.
                  </SettingOptionDescription>
                </Label>
                {settings[SettingOption.AIAgentMode] && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Select<SelectOption<AIModel>>
                      className='w-100 mt-2'
                      options={modelOptions}
                      value={modelOptions.find((option) => option.value === settings[SettingOption.AIAgentModeOptions]?.model) ?? null}
                      onChange={(selected) => {
                        setSettings((prevSettings) => ({
                          ...prevSettings,
                          [SettingOption.AIAgentModeOptions]: {
                            model: selected?.value ?? DEFAULT_MODEL,
                          },
                        }));
                      }}
                      isDisabled={!settings[SettingOption.AIAgentMode]}
                    />
                  </div>
                )}
              </div>
            </FormGroup>
          </>
        )}
        <BasicAlert type='info' className='mt-3 px-3 py-2'>
          <div className='d-flex align-items-start'>
            <i className='fal fa-info-circle mr-2 mt-1' />
            <div className='text-sm'>
              <strong>Privacy Protection:</strong> {AI_MESSAGE.EXCLUDED_METRIC_PLURAL}
            </div>
          </div>
        </BasicAlert>
      </ModalBody>
      <ModalFooter className='pt-3'>
        <Button color='link-secondary' className='me-3' onClick={toggle}>
          Cancel
        </Button>
        <Button data-testid='submit-auto-answer' color='primary' onClick={() => handleSubmit(settings)}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};
